import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { researchStudyService } from '../services/researchStudyService'
import { ResearchStudyFormData } from '../types/researchStudy'
import './ResearchStudyNew.css'

export function ResearchStudyNew() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<ResearchStudyFormData>({
    name: '',
    description: '',
    status: 'draft',
    protocol: {
      title: '',
      description: '',
      objectives: [],
      inclusionCriteria: [],
      exclusionCriteria: [],
      methodology: '',
      endpoints: [],
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: ResearchStudyFormData) => researchStudyService.create(data),
    onSuccess: (study) => {
      queryClient.invalidateQueries({ queryKey: ['research-studies'] })
      navigate(`/research-studies/${study.id}`)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate(formData)
  }

  return (
    <div className="research-study-new-page">
      <div className="page-header">
        <h1>Create New Research Study</h1>
        <button className="btn-secondary" onClick={() => navigate('/research-studies')}>
          Cancel
        </button>
      </div>

      <form onSubmit={handleSubmit} className="research-study-form">
        <div className="form-group">
          <label htmlFor="name">
            Study Name <span className="required">*</span>
          </label>
          <input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            placeholder="e.g., Efficacy of New Treatment for Condition X"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            placeholder="Brief description of the research study"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
            >
              <option value="draft">Draft</option>
              <option value="recruiting">Recruiting</option>
              <option value="active">Active</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="principalInvestigator">Principal Investigator ID</label>
            <input
              id="principalInvestigator"
              type="text"
              value={formData.principalInvestigator || ''}
              onChange={(e) => setFormData({ ...formData, principalInvestigator: e.target.value })}
              placeholder="User ID"
            />
          </div>

          <div className="form-group">
            <label htmlFor="institution">Institution</label>
            <input
              id="institution"
              type="text"
              value={formData.institution || ''}
              onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
              placeholder="e.g., University Hospital"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="irbApprovalNumber">IRB Approval Number</label>
            <input
              id="irbApprovalNumber"
              type="text"
              value={formData.irbApprovalNumber || ''}
              onChange={(e) => setFormData({ ...formData, irbApprovalNumber: e.target.value })}
              placeholder="IRB-2024-001"
            />
          </div>

          <div className="form-group">
            <label htmlFor="irbApprovalDate">IRB Approval Date</label>
            <input
              id="irbApprovalDate"
              type="date"
              value={formData.irbApprovalDate || ''}
              onChange={(e) => setFormData({ ...formData, irbApprovalDate: e.target.value })}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="startDate">Start Date</label>
            <input
              id="startDate"
              type="date"
              value={formData.startDate || ''}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="endDate">End Date</label>
            <input
              id="endDate"
              type="date"
              value={formData.endDate || ''}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="enrollmentTarget">Enrollment Target</label>
            <input
              id="enrollmentTarget"
              type="number"
              value={formData.enrollmentTarget || ''}
              onChange={(e) =>
                setFormData({ ...formData, enrollmentTarget: parseInt(e.target.value) || undefined })
              }
              placeholder="100"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Protocol Information</h3>

          <div className="form-group">
            <label htmlFor="protocolTitle">Protocol Title</label>
            <input
              id="protocolTitle"
              type="text"
              value={formData.protocol.title}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  protocol: { ...formData.protocol, title: e.target.value },
                })
              }
              placeholder="Protocol title"
            />
          </div>

          <div className="form-group">
            <label htmlFor="protocolDescription">Protocol Description</label>
            <textarea
              id="protocolDescription"
              value={formData.protocol.description}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  protocol: { ...formData.protocol, description: e.target.value },
                })
              }
              rows={4}
              placeholder="Detailed protocol description"
            />
          </div>

          <div className="form-group">
            <label htmlFor="methodology">Methodology</label>
            <textarea
              id="methodology"
              value={formData.protocol.methodology}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  protocol: { ...formData.protocol, methodology: e.target.value },
                })
              }
              rows={4}
              placeholder="Study methodology"
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/research-studies')}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create Study'}
          </button>
        </div>
      </form>
    </div>
  )
}
